import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, ICenterAnatomyPrefsDataResponse } from '@nkr/store/index';
import { IAnatomyPrefsData, IListItem, IUpdateAnatomyPrefsDataRequest } from '@nkr/models/index';
import { IErrorItem } from '../../error-item';
import { initialAnatomyPrefsData } from '../initial-data';
import { Alert, Col, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';
import { FormWrapper } from '../styles';
import CenterProfileNav from '../edit-nav';
import { profileNavKey } from '../../profile-nav-key';
import { toast } from 'react-toastify';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    anatomyMaxPreferenceLeftList: IListItem[],
    anatomyMaxPreferenceRightList: IListItem[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number) => Promise<ICenterAnatomyPrefsDataResponse>,
    updateData: (data: IUpdateAnatomyPrefsDataRequest) => Promise<ICenterAnatomyPrefsDataResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: IAnatomyPrefsData,
    hash: string,
    isDirty: boolean,
    isValid: boolean
}

class CenterAnatomyPrefsTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialAnatomyPrefsData,
        hash: '',
        isDirty: false,
        isValid: true
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting center\'s profile, one moment please...').then(() => {
            this.props.getData(this.props.centerId).then(response => {
                this.setState({
                    data: response.data.results,
                    hash: hash(response.data.results)
                }, () => {
                    let needsUpdate = this.state.data.complianceWaitListAnatomy?.failedChecks > 0 ? true : false;
                    this.props.onDataChanged(needsUpdate);
                });

                this.props.hideLoading();
            });
        });
    }

    handleSave = () => {
        let {
            anatomyPrefArteriesLeft,
            anatomyPrefArteriesRight,
            anatomyPrefVeinsLeft,
            anatomyPrefVeinsRight
        } = this.state.data;

        let data: IUpdateAnatomyPrefsDataRequest = {
            id: this.props.centerId,
            anatomyPrefArteriesLeft,
            anatomyPrefArteriesRight,
            anatomyPrefVeinsLeft,
            anatomyPrefVeinsRight
        };

        this.props.showLoading('Saving center LAB data, one moment please...').then(() => {
            this.props.updateData(data).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        data: response.data.results,
                        hash: hash(response.data.results),
                    }, () => {
                        toast.success('Center Lab data updated successfully');
                        let needsUpdate = this.state.data.complianceWaitListAnatomy?.failedChecks > 0 ? true : false;
                        this.props.onDataChanged(needsUpdate);
                    });
                }
            });
        });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(this.state.data);

        data[name] = parseInt(value);

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash);
        });
    }

    render = () => {
        const { anatomyMaxPreferenceRightList, anatomyMaxPreferenceLeftList } = this.props;
        const { anatomyPrefArteriesLeft, anatomyPrefArteriesRight, anatomyPrefVeinsLeft, anatomyPrefVeinsRight, complianceWaitListAnatomy } = this.state.data;

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.ANATOMY_PREFS}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.handleSave}
                />

                <div style={{ padding: '10px 10px 10px 10px', borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4, minHeight: 500 }}>
                    {complianceWaitListAnatomy?.complianceIssues?.length > 0 && (
                        <Alert bsStyle="danger" bsSize="sm">
                            <div>Please address the following compliance issues for Functional Waitlist Anatomy Preferences:</div>
                            <ul>
                                {complianceWaitListAnatomy.complianceIssues.map((message, idx) => (
                                    <li key={idx}>{message}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <h4 style={{ marginBottom: 16 }}>Functional Anatomy preferences for chain end waitlist offers</h4>

                    <FormWrapper horizontal>
                        <FormGroup>
                            <Col xs={12} sm={2} componentClass={ControlLabel}>Max Arteries Left Kidney:</Col>
                            <Col xs={12} sm={2}>
                                <FormControl
                                    componentClass='select'
                                    name='anatomyPrefArteriesLeft'
                                    value={anatomyPrefArteriesLeft}
                                    onChange={this.handleFieldChange}
                                >
                                    <option>Unknown</option>
                                    {anatomyMaxPreferenceLeftList.map(item =>
                                        <option value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>

                            <Col xs={12} sm={2} smOffset={1} componentClass={ControlLabel}>Max Arteries Right Kidney:</Col>
                            <Col xs={12} sm={2}>
                                <FormControl
                                    componentClass='select'
                                    name='anatomyPrefArteriesRight'
                                    value={anatomyPrefArteriesRight}
                                    onChange={this.handleFieldChange}
                                >
                                    <option>Unknown</option>
                                    {anatomyMaxPreferenceRightList.map(item =>
                                        <option value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} sm={2} componentClass={ControlLabel}>Max Veins Left Kidney:</Col>
                            <Col xs={12} sm={2}>
                                <FormControl
                                    componentClass='select'
                                    name='anatomyPrefVeinsLeft'
                                    value={anatomyPrefVeinsLeft}
                                    onChange={this.handleFieldChange}
                                >
                                    <option>Unknown</option>
                                    {anatomyMaxPreferenceLeftList.map(item =>
                                        <option value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>

                            <Col xs={12} sm={2} smOffset={1} componentClass={ControlLabel}>Max Veins Right Kidney:</Col>
                            <Col xs={12} sm={2}>
                                <FormControl
                                    componentClass='select'
                                    name='anatomyPrefVeinsRight'
                                    value={anatomyPrefVeinsRight}
                                    onChange={this.handleFieldChange}
                                >
                                    <option>Unknown</option>
                                    {anatomyMaxPreferenceRightList.map(item =>
                                        <option value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                    </FormWrapper>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId) => {
            return dispatch(CenterActionCreators.getAnatomyPrefsData(centerId));
        },
        updateData: (data) => {
            return dispatch(CenterActionCreators.updateAnatomyPrefsData(data));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterAnatomyPrefsTab);